import React, { useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { clearRecurringTask, loadRecurringTask } from '../state';
import { useGetRecurringTaskByIdQuery } from '../../../services/RecurringTaskService';
import { PageContainer, PageCard } from '../../common/components';
import { RecurringTaskEditHeader } from '../components/RecurringTaskForm/headers';
import { RecurringTaskEditForm } from '../components/RecurringTaskForm';

/**
 * RecurringTaskEdit - Main page component for editing existing recurring tasks
 */
const RecurringTaskEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('id');
  const dispatch = useAppDispatch();

  // Query to load the recurring task by ID
  const {
    data: recurringTask,
    isLoading,
    isError,
    error,
  } = useGetRecurringTaskByIdQuery(taskId || '', {
    skip: !taskId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    // Clear state when task ID changes
    if (taskId) {
      dispatch(clearRecurringTask());
    }
  }, [dispatch, taskId]);

  useEffect(() => {
    // Load recurring task into state when data is fetched
    if (recurringTask) {
      dispatch(loadRecurringTask(recurringTask));
    }
  }, [dispatch, recurringTask]);

  // Show loading state
  if (isLoading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
            <Box mt={2} color="#64748b">
              Loading recurring task...
            </Box>
          </Box>
        </Box>
      </PageContainer>
    );
  }

  // Show error state
  if (isError || !taskId) {
    const errorMessage = !taskId 
      ? 'No recurring task ID provided. Please provide a valid ID in the URL.'
      : 'Failed to load recurring task. Please check the ID and try again.';

    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" mt={4}>
          <Alert 
            severity="error" 
            sx={{ 
              maxWidth: 600,
              '& .MuiAlert-message': { fontSize: '1rem' }
            }}
          >
            {errorMessage}
            {isError && error && 'message' in error && (
              <Box mt={1} color="#b91c1c" fontSize="0.875rem">
                Details: {error.message}
              </Box>
            )}
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  // Show main edit interface
  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <RecurringTaskEditHeader />
          <RecurringTaskEditForm />
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default RecurringTaskEdit;
